import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { RiskFormModal } from '../features/risks/RiskFormModal';
import { createRisk, fetchRisks } from '../features/risks/risksApi';
import type { RiskEntry } from '../features/risks/types';
import { StudyActivityFormModal } from '../features/riskStudies/StudyActivityFormModal';
import { StudyActivitySheet } from '../features/riskStudies/StudyActivitySheet';
import {
  createStudyActivity,
  deleteStudyActivity,
  fetchRiskStudy,
  fetchStudyActivities,
  fetchStudySummary,
  setStudyStatus,
  updateStudyActivity,
} from '../features/riskStudies/riskStudiesApi';
import type { RiskStudy, StudyActivity, StudyStatus, StudySummary } from '../features/riskStudies/types';
import { STUDY_STATUS_LABEL } from '../features/riskStudies/types';

type ActivityFormMode =
  | { kind: 'create'; parentId: string | null }
  | { kind: 'edit'; activity: StudyActivity };

const STUDY_STATUSES: StudyStatus[] = ['borrador', 'vigente', 'revision', 'archivado'];

function findActivity(nodes: StudyActivity[], id: string): StudyActivity | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findActivity(node.children, id);
    if (found) return found;
  }
  return null;
}

export function RiskStudyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { confirm } = useConfirmDialog();
  const { canEdit } = usePlatformAccessLevel('risk_studies.access');
  const [study, setStudy] = useState<RiskStudy | null>(null);
  const [activities, setActivities] = useState<StudyActivity[]>([]);
  const [studyRisks, setStudyRisks] = useState<RiskEntry[]>([]);
  const [summary, setSummary] = useState<StudySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activityForm, setActivityForm] = useState<ActivityFormMode | null>(null);
  const [riskActivity, setRiskActivity] = useState<StudyActivity | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [s, acts, sum, risksResult] = await Promise.all([
        fetchRiskStudy(id),
        fetchStudyActivities(id),
        fetchStudySummary(id),
        fetchRisks({ studyId: id, pageSize: 200 }),
      ]);
      setStudy(s);
      setActivities(acts);
      setSummary(sum);
      setStudyRisks(risksResult.data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const risksByActivity = useMemo(() => {
    const map: Record<string, RiskEntry[]> = {};
    for (const risk of studyRisks) {
      if (!risk.activityId) continue;
      if (!map[risk.activityId]) map[risk.activityId] = [];
      map[risk.activityId].push(risk);
    }
    return map;
  }, [studyRisks]);

  const activityFormMeta = useMemo(() => {
    if (!activityForm) return null;
    if (activityForm.kind === 'edit') {
      return {
        title: 'Editar actividad',
        description: `Ítem ${activityForm.activity.outlineNo}`,
        initialName: activityForm.activity.name,
      };
    }
    if (activityForm.parentId) {
      const parent = findActivity(activities, activityForm.parentId);
      return {
        title: 'Nueva subactividad',
        description: parent
          ? `Se agregará como hijo de ${parent.outlineNo} — ${parent.name}`
          : 'Se agregará como subactividad del ítem seleccionado.',
        initialName: '',
      };
    }
    return {
      title: 'Nueva actividad raíz',
      description: 'Se numerará como 1.0, 2.0, 3.0… según el orden en la planilla.',
      initialName: '',
    };
  }, [activityForm, activities]);

  const handleActivitySubmit = async (name: string) => {
    if (!id || !activityForm) return;
    if (activityForm.kind === 'edit') {
      await updateStudyActivity(id, activityForm.activity.id, { name });
    } else {
      await createStudyActivity(id, { parentId: activityForm.parentId, name });
    }
    await load();
  };

  const handleDelete = async (activity: StudyActivity) => {
    if (!id) return;
    const ok = await confirm({
      title: 'Eliminar actividad',
      message: `¿Eliminar la actividad ${activity.outlineNo} — ${activity.name}?`,
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;
    setDeleteError(null);
    try {
      await deleteStudyActivity(id, activity.id);
      await load();
    } catch {
      setDeleteError('No se puede eliminar: hay riesgos vinculados a esta actividad.');
    }
  };

  const handleStatusChange = async (status: StudyStatus) => {
    if (!id || !study || study.status === status) return;
    setStatusSaving(true);
    try {
      await setStudyStatus(id, status);
      await load();
    } finally {
      setStatusSaving(false);
    }
  };

  const handleCreateRisk = async (payload: Parameters<typeof createRisk>[0]) => {
    await createRisk({
      ...payload,
      studyId: id,
      activityId: riskActivity?.id ?? payload.activityId,
      scope: 'operativo',
      orgUnitId: study?.orgUnitId ?? payload.orgUnitId,
      evaluationMethodId: study?.evaluationMethodId ?? payload.evaluationMethodId,
    });
    setRiskActivity(null);
    await load();
  };

  if (loading) return <p className='p-6'>Cargando…</p>;
  if (!study) return <p className='p-6'>Estudio no encontrado.</p>;

  return (
    <div className='mx-auto max-w-6xl p-6'>
      <Link to='/estudios-riesgo' className='text-sm text-primary'>
        ← Estudios
      </Link>
      <div className='mt-4 mb-6 flex flex-wrap items-start justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold'>
            {study.code} — {study.title}
          </h1>
          <p className='text-sm text-muted-foreground'>
            {study.orgUnit?.name} · {STUDY_STATUS_LABEL[study.status]} ·{' '}
            {study.evaluationMethod?.name}
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {canEdit && (
            <select
              className='rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={study.status}
              disabled={statusSaving}
              onChange={(e) => void handleStatusChange(e.target.value as StudyStatus)}
              aria-label='Estado del estudio'
            >
              {STUDY_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STUDY_STATUS_LABEL[status]}
                </option>
              ))}
            </select>
          )}
          <Link
            to={`/riesgos/tablero?orgUnitId=${study.orgUnitId}`}
            className='rounded-md border border-input px-3 py-2 text-sm hover:bg-muted'
          >
            Ver en tablero MAP
          </Link>
        </div>
      </div>

      {summary && (
        <div className='mb-6 grid grid-cols-3 gap-4 rounded border border-border bg-card p-4 text-sm'>
          <div>
            <p className='text-muted-foreground'>Riesgos</p>
            <p className='text-xl font-semibold'>{summary.totalRisks}</p>
          </div>
          <div>
            <p className='text-muted-foreground'>Por nivel</p>
            <p>{Object.entries(summary.byLevel).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}</p>
          </div>
          <div>
            <p className='text-muted-foreground'>Con acción</p>
            <p className='text-xl font-semibold'>{summary.withActionsPercent}%</p>
          </div>
        </div>
      )}

      <h2 className='mb-2 text-lg font-semibold'>Planilla por actividad (AMFE)</h2>
      {deleteError && (
        <p className='mb-3 text-sm text-destructive' role='alert'>
          {deleteError}
        </p>
      )}
      <StudyActivitySheet
        activities={activities}
        risksByActivity={risksByActivity}
        canEdit={canEdit}
        onAdd={(parentId) => setActivityForm({ kind: 'create', parentId })}
        onEdit={(activity) => setActivityForm({ kind: 'edit', activity })}
        onDelete={(activity) => void handleDelete(activity)}
        onAddRisk={(activity) => setRiskActivity(activity)}
      />

      <StudyActivityFormModal
        open={activityForm !== null}
        onOpenChange={(open) => {
          if (!open) setActivityForm(null);
        }}
        title={activityFormMeta?.title ?? 'Actividad'}
        description={activityFormMeta?.description}
        initialName={activityFormMeta?.initialName ?? ''}
        onSubmit={handleActivitySubmit}
      />

      <RiskFormModal
        open={riskActivity !== null}
        onOpenChange={(open) => {
          if (!open) setRiskActivity(null);
        }}
        prefill={
          riskActivity && study
            ? {
                scope: 'operativo',
                studyId: study.id,
                activityId: riskActivity.id,
                evaluationMethodId: study.evaluationMethodId,
                title: riskActivity.name,
              }
            : undefined
        }
        onSubmit={handleCreateRisk}
      />
    </div>
  );
}
